import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager, {IResourceManager} from '@/Management/ResourceManager';
import TextBoxViewModelType from '@/ViewModels/Controls/TextBoxViewModel';
import DropDownListViewModelType from '@/ViewModels/Controls/DropDownListViewModel';
import RADApplication from '@/Management/RADApplication';

/**
* Das ViewModel für die MLStringBox
*/
export default class MLStringBoxViewModel extends ControlViewModel {

    private DropDownListViewModel = ComponentManager.getViewModel<typeof DropDownListViewModelType>('DropDownListViewModel');

    /**
    * Ein internes Objekt zur Verwaltung der Eingaben
    */
    private languageDict: Object;

    /**
    * Gibt an, ob der Wert des DropDowns verändert wurde
    */
    private hasSelectedLanguageChanged: boolean;

    /**
    * Gibt an, ob der Text für alle Sprachen identisch ist
    */
    public isEachLanguageEqual: KnockoutObservable<boolean>;

    /**
    * Definiert den Separator-String
    */
    public separatorString: KnockoutObservable<string>;

    /**
    * Das ViewModel für die TextBox
    */
    public textBox: TextBoxViewModelType;

    /**
    * Gibt an, ob die DropDownList fokussierbar ist
    */
    public isDropdownFocusable: boolean;

    /**
    * Das ViewModel für die DropDownList
    */
    public dropDownList: DropDownListViewModelType;

    /**
    * Der Wert der MLStringBox
    */
    public value: KnockoutComputed<string>

    /**
    * Intern verwendetes Feld
    */
    private readDependency: KnockoutObservable<boolean>;

    public constructor(params) {
        super(params);

        this.languageDict = {};
        this.hasSelectedLanguageChanged = false;
        this.isEachLanguageEqual = this.observableOrDefault(params.isEachLanguageEqual, false);
        this.separatorString = this.observableOrDefault(params.separatorString, '');
        this.textBox = this.paramOrDefault(params.textBox, null);
        this.isDropdownFocusable = this.paramOrDefault(params.isDropdownFocusable, null);
        this.dropDownList = this.paramOrDefault(params.dropDownList, this.createDefaultDropDownListViewModel());

        // Die Language-Observables sollen hier schon erzeugt werden, damit Knockout die Abhängigkeit der Read-Methode
        // zu diesen erkennt; ansonsten würden sie nämlich erst in der read-Methode erzeugt werden und dort nicht als Abhängigkeit aufgenommen werden.
        // Dadurch wird value auch direkt aktualisiert, wenn sich einer der sprachspezifischen Werte ändert
        for (let i = 0; i < RADApplication.getInstance().configuration.languages.length; i++) {
            const language = RADApplication.getInstance().configuration.languages[i];
            this.getLanguageValue(language.iso6391);
        }

        // Wird der Wert der TextBox verändert, wird dieser unter der aktuellen Sprache im Dictionary gespeichert
        this.defineSubscription(this.textBox.value, (value: string) => {
            const language = this.dropDownList.value();
            this.setLanguageValue(value, language);
        });

        // Wird die Auswahl der DropDownList verändert, wird der Wert in der ausgewählten Sprache in die TextBox geschrieben
        this.defineSubscription(this.dropDownList.value, (language: string) => {
            const value = this.getLanguageValue(language);
            this.textBox.language(language);
            this.setTextBoxValue(value);

            this.hasSelectedLanguageChanged = true;
        });

        // Damit wir beim Schreiben des Gesamtwertes via write nicht temporäre Werte an die Subscriber (insbesondere value selbst)
        // übermitteln, können wir hiermit die read-Methode temporär ausschalten und dann manuell triggern.
        this.readDependency = this.observableOrDefault(true).extend({ notify: 'always' });
        this.readDependency.allowRead = true;
        this.readDependency.cacheValue = null;

        this.value = this.defineComputed({
            read: () => {
                // Knockout die Abhängigkeit erkennen lassen, um sie triggern zu können
                this.readDependency();

                const separatorString = this.separatorString();

                let result: string;
                let parts = [];

                for (let i = 0; i < RADApplication.getInstance().configuration.languages.length; i++) {
                    const language = RADApplication.getInstance().configuration.languages[i];
                    const languageValue = this.getLanguageValue(language.iso6391);
                    if(languageValue !== '')
                        parts.push(language.iso6391 + ':' + languageValue);
                }

                if (parts.length === 0) {
                    result = '';
                }
                else {
                    result = separatorString + parts.join(separatorString);
                }

                if (!this.readDependency.allowRead) { // Ist das ein durch write/knockout INDIREKT getriggerter Aufruf? Wenn ja, dann noch alten Wert zurückgeben.
                    return this.readDependency.cacheValue;
                }

                this.readDependency.cacheValue = result; // Wert merken, um ihn in Schreibphasen zurückgeben zu können
                return result;
            },
            write: (value: string) => {
                value = value || '';

                const isEachLanguageEqual = this.isEachLanguageEqual();
                const separatorString = this.separatorString();

                if (isEachLanguageEqual === true && value.indexOf(separatorString) === -1) {
                    this.setTextBoxValue(value);
                    for (let i = 0; i < RADApplication.getInstance().configuration.languages.length; i++) {
                        const language = RADApplication.getInstance().configuration.languages[i];
                        this.setLanguageValue(value, language.iso6391);
                    }
                } else {
                    let languageValues = value.split(separatorString);
                    for (let i = languageValues.length - 1; i >= 0; i--) {
                        if (languageValues[i] == null || languageValues[i] === '') {
                            languageValues.splice(i, 1);
                        }
                    }

                    // Da read von den einzelnen Sprachwerten abhängt, schalten wir die Evaluierung hier temporär aus.
                    // Ansonsten würden schon durch das initiale Setzen den Subsribern von params.value falsche Werte
                    // übermittelt werden (dieses Observable wird mit params.value synchronisiert).
                    this.readDependency.allowRead = false;

                    if (languageValues.length > 0) {
                        for (let i = 0; i < RADApplication.getInstance().configuration.languages.length; i++) {
                            const language = RADApplication.getInstance().configuration.languages[i];
                            let val = '';
                            for (let j = 0; j < languageValues.length; j++) {
                                const languageValue = languageValues[j]; // like: DE:example-text
                                const index = languageValue.indexOf(":");
                                const mlValue = languageValue.substring(index + 1, languageValue.length); // like: 'example-text'
                                const mlLang = languageValue.substring(0, index); // like: 'DE'

                                if (mlLang === language.iso6391) {
                                    val = mlValue;
                                    break;
                                }
                            }
                            this.setLanguageValue(val, language.iso6391);
                        }
                    }

                    // Die TextBox soll synchron mit dem Wert der aktuellen Sprache sein
                    const currentLanguage = this.dropDownList.value();
                    const currentLanguageValue = this.getLanguageValue(currentLanguage);
                    this.setTextBoxValue(currentLanguageValue);

                    // Wert des Observables soll jetzt aktualisiert werden (in Knockout gespeichertes latestValue ist nicht automatisch der an write übergebene Wert!)
                    this.readDependency.allowRead = true;
                    this.readDependency(true);
                }
            }
        }, params.value);

        ControlViewModel.checkProperties(this, params);
    }

    private createDefaultDropDownListViewModel(): DropDownListViewModelType {
        const resourceManager = ResourceManager.getInstance();
        
        const languageData = resourceManager.language.data();
        for (const item of languageData)
            item.altText = MLStringBoxViewModel.resolveAltText(resourceManager, item.fullName);
        
        return new this.DropDownListViewModel({
            parentLocator: this.locator,
            locator: 'Language',
            value: resourceManager.getLanguage().iso6391,
            data: languageData,
            dataTextField: 'fullName',
            isFocusable: this.isDropdownFocusable,
            dataValueField: 'iso6391',
            // Wir setzen den "richtigen" alt-Tag nur im ausgewählten Fall, da dort nur die Flagge dargestellt wird
            // Die Optionen im Dropdown bekommen den "leeren" alt-Tag, um der Konvention zu folgen, ein Screenreader würde sonst doppelt vorlesen
            template: '# if (data && data.iconUrl && data.fullName) { #<img src=\"#:data.iconUrl#\" alt=\"\" /><span>#:data.fullName#</span># } #',
            valueTemplate: '# if (data && data.iconUrl) { #<img src=\"#:data.iconUrl#\" alt=\"#:data.altText#\" /># } #',
            isAutoWidth: true,
            itemLocatorStrategy: (item) => item.iso6391
        });
    }

    /**
    * Dient dem Setzen des Wertes der Haupt-TextBox, ohne dass ggf. initiale null-Werte durch einen leeren String ersetzt werden,
    * was zu unerwünschten Benachrichtungen via dessen value-Observable führen würde
    */
    private setTextBoxValue(value) {
        if (value == null) {
            value = '';
        }

        const currentValue = this.textBox.value();
        if (currentValue == null && value == '') {
            return;
        }

        this.textBox.value(value);
    }
    
    private static resolveAltText(resourceManager: IResourceManager, fullName: string): string {
        const resourceItem = resourceManager.get('ARIA_MLSTRING_SELECTED_LANGUAGE');
        return resourceItem.replace('$$language$$', fullName);
    }

    /**
    * Hilfsfunktion zum Setzen eines Sprachwerts
    */
    private setLanguageValue(value, iso6391) {
        if (iso6391 in this.languageDict) {
            this.languageDict[iso6391](value);
        } else {
            this.languageDict[iso6391] = this.observableOrDefault(value);
        }
    }

    /**
     * Hilfsfunktion zum Auslesen eines Sprachwerts
     */
    private getLanguageValue(iso6391) {
        if (iso6391 in this.languageDict) {
            return this.languageDict[iso6391]();
        } else {
            const value = '';
            this.languageDict[iso6391] = this.observableOrDefault(value);

            return value;
        }
    }

    /**
     * @override
     * @inheritdoc 
     */
    public resetValue() {
        this.separatorString(null);
        this.value(null);
    }
}

ComponentManager.setViewModel('MLStringBoxViewModel', MLStringBoxViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');